/**
 * Utilities for Chase the Ace game configuration.
 *
 * The target card (winning card) is stored in the `cards_remaining` section
 * config as `{ rank, suit }` (e.g., `{ rank: '8', suit: '♥' }`).
 * All CTA layout files should use these helpers instead of hardcoding A♠.
 */

const SUIT_NAMES = {
  en: { '♠': 'Spades', '♥': 'Hearts', '♦': 'Diamonds', '♣': 'Clubs' },
  fr: { '♠': 'Pique', '♥': 'Cœur', '♦': 'Carreau', '♣': 'Trèfle' },
  es: { '♠': 'Picas', '♥': 'Corazones', '♦': 'Diamantes', '♣': 'Tréboles' },
};

const RANK_NAMES = {
  en: { A: 'Ace', J: 'Jack', Q: 'Queen', K: 'King' },
  fr: { A: 'As', J: 'Valet', Q: 'Dame', K: 'Roi' },
  es: { A: 'As', J: 'Jota', Q: 'Reina', K: 'Rey' },
};

const OF_WORD = { en: 'of', fr: 'de', es: 'de' };

/** Default target card — Ace of Spades */
export const DEFAULT_TARGET_CARD = { rank: 'A', suit: '♠' };

// Map API suit names (stored as words) to glyph symbols
const SUIT_NAME_TO_GLYPH = {
  spades: '♠', spade: '♠',
  hearts: '♥', heart: '♥',
  diamonds: '♦', diamond: '♦',
  clubs: '♣', club: '♣',
};

// Map API rank names (stored as words) to display values
const RANK_NAME_TO_SYMBOL = {
  ace: 'A', jack: 'J', queen: 'Q', king: 'K',
};

/**
 * Resolve the target (winning) card from the sections array.
 * Falls back to siteDetails.winningCardNumber/WinningCardSuit from the API,
 * then to the default Ace of Spades if nothing is configured.
 *
 * @param {Array} sections - page sections array
 * @param {object} [siteDetails] - optional siteDetails from useSiteConfig()
 * @returns {{ rank: string, suit: string }}
 */
export function resolveCtaTargetCard(sections, siteDetails) {
  const section = Array.isArray(sections)
    ? sections.find(s => s.section_type === 'cards_remaining')
    : null;
  const cfg = section?.config?.targetCard;
  if (cfg && cfg.rank && cfg.suit) return { rank: cfg.rank, suit: cfg.suit };

  // Try the API-provided winning card from siteDetails
  const rawRank = siteDetails?.winningCardNumber ?? siteDetails?.WinningCardNumber;
  const rawSuit = siteDetails?.winningCardSuit ?? siteDetails?.WinningCardSuit;
  if (rawRank && rawSuit) {
    // rawRank may be a full card name like "Ace of Hearts" — normalise to first word
    const rawRankKey = String(rawRank).toLowerCase().split(/\s+/)[0];
    const rank = RANK_NAME_TO_SYMBOL[rawRankKey] ?? RANK_NAME_TO_SYMBOL[String(rawRank).toLowerCase()] ?? String(rawRank);
    const suit = SUIT_NAME_TO_GLYPH[String(rawSuit).toLowerCase()] ?? rawSuit;
    if (rank && suit) return { rank, suit };
  }

  return DEFAULT_TARGET_CARD;
}

/**
 * Format target card as a human-readable name, e.g. "8 of Hearts" or "Roi de Cœur".
 * Pass `language` ('en', 'fr', 'es') to get a localised name.
 *
 * @param {string} rank
 * @param {string} suit  (suit symbol: ♠ ♥ ♦ ♣)
 * @param {string} [language='en']
 * @returns {string}
 */
export function formatTargetCardName(rank, suit, language = 'en') {
  const lang = language?.toLowerCase()?.startsWith('fr') ? 'fr'
    : language?.toLowerCase()?.startsWith('es') ? 'es' : 'en';
  const rankDisplay = RANK_NAMES[lang]?.[rank] ?? RANK_NAMES.en[rank] ?? rank;
  const suitDisplay = SUIT_NAMES[lang]?.[suit] ?? SUIT_NAMES.en[suit] ?? suit;
  const of = OF_WORD[lang] ?? 'of';
  return `${rankDisplay} ${of} ${suitDisplay}`;
}

export function isDefaultCtaTargetCard(targetCard) {
  return targetCard?.rank === DEFAULT_TARGET_CARD.rank && targetCard?.suit === DEFAULT_TARGET_CARD.suit;
}

/**
 * Formats copy that names the Chase the Ace jackpot card.
 * Supports the preferred `{{targetCard}}` placeholder while also correcting
 * legacy content that still says Ace of Spades after the target card changes.
 */
export function formatCtaTargetCardText(value, targetCard = DEFAULT_TARGET_CARD, fallback = '', language = 'en') {
  const source = value || fallback || '';
  const targetName = formatTargetCardName(targetCard.rank, targetCard.suit, language);
  const targetNameWithSuit = `${targetName} ${targetCard.suit}`;

  let result = source
    .replaceAll('{{targetCard}}', targetName)
    .replaceAll('{{targetCardWithSuit}}', targetNameWithSuit);

  if (!isDefaultCtaTargetCard(targetCard)) {
    result = result
      .replaceAll('Ace of Spades ♠', targetNameWithSuit)
      .replaceAll('Ace of Spades', targetName)
      .replaceAll('ace of spades', targetName.toLowerCase());

    // Also replace standalone "the Ace" / "The Ace" for short labels like "Find the Ace!"
    // Ensure we do not recursively match if "the Ace" is already part of "the Ace of Hearts" or "l'As de Cœur"
    const pattern1 = new RegExp('\\bThe Ace\\b(?!\\s+(of|de))', 'g');
    const pattern2 = new RegExp('\\bthe Ace\\b(?!\\s+(of|de))', 'g');
    const pattern3 = new RegExp('\\bthe ace\\b(?!\\s+(of|de))', 'g');

    result = result
      .replace(pattern1, `The ${targetName}`)
      .replace(pattern2, `the ${targetName}`)
      .replace(pattern3, `the ${targetName.toLowerCase()}`);
  }

  return result;
}

/**
 * Build the compact card string used in the eliminatedCards array,
 * e.g. 'A♠', '8♥'.
 */
export function targetCardString({ rank, suit }) {
  return `${rank}${suit}`;
}

const normalizeCardPart = (value, aliases = {}) => {
  const normalized = String(value || '').trim().toLowerCase();
  return aliases[normalized] || normalized.toUpperCase();
};

const normalizeCard = (rank, suit) => `${normalizeCardPart(rank, RANK_NAME_TO_SYMBOL)}${normalizeCardPart(suit, SUIT_NAME_TO_GLYPH)}`;

export function isCtaTargetCardFound({ targetCard, eliminatedCards = [], drawResult = null, jackpotWon = false } = {}) {
  if (!targetCard?.rank || !targetCard?.suit) return false;
  if (jackpotWon === true) return true;

  const target = normalizeCard(targetCard.rank, targetCard.suit);
  const resultRank = drawResult?.cardDrawnRank ?? drawResult?.cardNumber ?? drawResult?.CardNumber;
  const resultSuit = drawResult?.cardDrawnSuit ?? drawResult?.cardSuit ?? drawResult?.CardSuit;
  if (resultRank && resultSuit && normalizeCard(resultRank, resultSuit) === target) return true;

  return eliminatedCards.some((card) => {
    if (typeof card === 'string') {
      const suit = card.slice(-1);
      return normalizeCard(card.slice(0, -1), suit) === target;
    }
    return normalizeCard(card?.rank ?? card?.cardNumber, card?.suit ?? card?.cardSuit) === target;
  });
}

export function resolveCtaFinalJackpot({ winners = [], targetCard, gameNumber = null } = {}) {
  if (!Array.isArray(winners) || !targetCard?.rank || !targetCard?.suit) return null;
  const target = normalizeCard(targetCard.rank, targetCard.suit);

  const winner = winners.find((record) => {
    const recordGameNumber = record?.gameNumber ?? record?.GameNumber ?? record?.game_number;
    if (gameNumber != null && (recordGameNumber == null || String(recordGameNumber) !== String(gameNumber))) return false;
    if (record?.additionalDrawId || record?.AdditionalDrawId || record?.isSecondaryPrize || record?.IsSecondaryPrize || record?.secondaryPrize) return false;
    const isWinner = record?.isWinner ?? record?.IsWinner;
    const status = String(record?.status ?? record?.Status ?? '').trim().toLowerCase();
    if (isWinner === false || (isWinner !== true && status !== 'winner')) return false;

    const rank = record?.cardNumber ?? record?.CardNumber ?? record?.cardDrawnRank;
    const suit = record?.cardSuit ?? record?.CardSuit ?? record?.cardDrawnSuit;
    return rank && suit && normalizeCard(rank, suit) === target;
  });

  const rawAmount = winner?.amount ?? winner?.Amount ?? winner?.prizeAmount ?? winner?.PrizeAmount;
  if (rawAmount == null) return null;
  const amount = Number(String(rawAmount).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

/**
 * Resolve the current envelope number and draw note from cards_remaining config.
 * Returns null values when not set.
 */
export function resolveCtaDrawResult(sections) {
  const section = Array.isArray(sections)
    ? sections.find(s => s.section_type === 'cards_remaining')
    : null;
  return {
    envelopeNumber: section?.config?.currentEnvelopeNumber ?? null,
    drawNote: section?.config?.currentDrawNote ?? null,
    cardDrawnRank: section?.config?.cardDrawnRank ?? section?.config?.currentEnvelopeCardRank ?? null,
    cardDrawnSuit: section?.config?.cardDrawnSuit ?? section?.config?.currentEnvelopeCardSuit ?? null,
    envelopeResultLocked: section?.config?.envelopeResultLocked === true,
  };
}

/**
 * Resolve whether CTA card selection happens manually at the event.
 * The dashboard saves this as an organization setting, but older draft section
 * configs may still use manualEnvelopeMode, so both shapes are accepted.
 */
export function resolveCtaManualCardMode(siteDetails, sections) {
  if (
    siteDetails?.isManualCardInEnvelopes === true ||
    siteDetails?.manualCardInEnvelopes === true ||
    siteDetails?.manualEnvelopeMode === true
  ) {
    return true;
  }

  const section = Array.isArray(sections)
    ? sections.find(s => s.section_type === 'cards_remaining')
    : null;
  return section?.config?.manualEnvelopeMode === true || section?.config?.isManualCardInEnvelopes === true;
}

export const resolveCtaManualEnvelopeMode = resolveCtaManualCardMode;

export function resolveCtaEncryptedEnvelopeMode(siteDetails, sections) {
  if (
    siteDetails?.isEncryptedEnvelopeSelection === true ||
    siteDetails?.encryptedEnvelopeSelection === true ||
    siteDetails?.manualEncryptedEnvelopeMode === true
  ) {
    return true;
  }

  const section = Array.isArray(sections)
    ? sections.find(s => s.section_type === 'cards_remaining')
    : null;
  return section?.config?.isEncryptedEnvelopeSelection === true || section?.config?.encryptedEnvelopeSelection === true;
}

export function resolveCtaEncryptedEnvelopeAutoFinalize(siteDetails, sections) {
  if (
    siteDetails?.isEncryptedEnvelopeAutoFinalize === true ||
    siteDetails?.encryptedEnvelopeAutoFinalize === true
  ) {
    return true;
  }

  const section = Array.isArray(sections)
    ? sections.find(s => s.section_type === 'cards_remaining')
    : null;
  return section?.config?.isEncryptedEnvelopeAutoFinalize === true || section?.config?.encryptedEnvelopeAutoFinalize === true;
}

export function resolveCtaPostDrawEnvelopeMode(siteDetails, sections) {
  return resolveCtaManualCardMode(siteDetails, sections) || resolveCtaEncryptedEnvelopeMode(siteDetails, sections);
}
